import React from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Business,
  Lock,
} from "@mui/icons-material";

import loginVideoWebm from "../../assets/login-video.webm";
import loginVideoMp4 from "../../assets/login-video-compressed.mp4";
import loginPoster from "../../assets/login-poster.webp";
import vyontaLogoBlanco from "../../assets/Vyonta-logo-blanco.png";

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    height: 50,
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#101828",

    "& fieldset": {
      borderColor: "#d7deea",
    },

    "&:hover fieldset": {
      borderColor: "#b8c4d6",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#0059f6",
      borderWidth: "1px",
    },

    "& .MuiOutlinedInput-input": {
      fontSize: 14,
      padding: "11px 12px",
      color: "#101828",

      "&::placeholder": {
        color: "#7b8798",
        opacity: 1,
      },
    },
  },
};

const loginButtonStyle = {
  height: 42,
  borderRadius: "6px",
  backgroundColor: "#0059f6",
  fontSize: 16,
  fontWeight: 800,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  boxShadow: "0 10px 22px rgba(7, 91, 255, 0.22)",

  "&:hover": {
    backgroundColor: "#004be0",
    boxShadow: "0 12px 26px rgba(7, 91, 255, 0.28)",
  },
};

export default function LoginUI({
  usuario,
  password,
  error,
  showPassword,
  onUsuarioChange,
  onPasswordChange,
  rememberLogin,
  onRememberLoginChange,
  onSubmit,
  handleShowPassword,
}) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "grid",
        gridTemplateColumns:"1.15fr 0.85fr",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Panel izquierdo con video */}
      <Box
        sx={{
          position: "relative",
          minHeight: "100dvh",
          overflow: "hidden",
        }}
      >
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={loginPoster}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        >
          <source src={loginVideoWebm} type="video/webm" />
          <source src={loginVideoMp4} type="video/mp4" />
        </Box>

        {/* Oscurecedor para que el texto se lea bien */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(95deg, rgba(0,0,0,.82) 10%, rgba(0,0,0,.35) 42%, rgba(0,0,0,.16) 100%)",
          }}
        />

        {/* Contenido sobre el video */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: 140,
              height: 56,
              position: "relative",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={vyontaLogoBlanco}
              alt="Vyonta"
              sx={{
                position: "absolute",
                width: 239,
                height: "auto",
                left: -48,
                top: -42,
                display: "block",
              }}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography
              sx={{
                color: "#fff",
                fontSize: 30,
                fontWeight: 400,
                lineHeight: 1.12,
                letterSpacing: "-0.8px",
              }}
            >
              Potenciá tu
            </Typography>
            
            <Typography
              sx={{
                color: "#fff",
                fontSize: 30,
                fontWeight: 800,
                lineHeight: 1.12,
                letterSpacing: "-0.8px",
                mt: "2px",
              }}
            >
              gestión comercial
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Panel derecho login */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexDirection: "column",
          pt: "16vh",
          background:
            "radial-gradient(circle at top right, rgba(7,91,255,.08), transparent 34%), #ffffff",
        }}
      >
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            width: "100%",
            maxWidth: 400,
          }}
        >
          <Typography
            sx={{
              color: "#070b33",
              fontSize: 35,
              fontWeight: 800,
              letterSpacing: "-1px",
              mb: 0.2,
            }}
          >
            Iniciar sesión
          </Typography>

          <Typography
            sx={{
              color: "#53607a",
              fontSize: 14,
              mb: 4,
            }}
          >
            Accedé a tu cuenta para continuar
          </Typography>


          <Typography
            sx={{
              color: "#070b33",
              fontSize: 14,
              fontWeight: 800,
              mb: 0.5,
            }}
          >
            Empresa
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Ingresá el nombre de tu empresa"
            value={usuario}
            onChange={onUsuarioChange}
            error={error}
            variant="outlined"
            sx={{ ...inputStyle, mb: 2 }}
            autoComplete="username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Business
                    fontSize="small"
                    sx={{ color: "#0059f6", mr: 0.5 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <Typography
            sx={{
              color: "#070b33",
              fontSize: 14,
              fontWeight: 800,
              mb: 0.5,
            }}
          >
            Contraseña
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Ingresá tu contraseña"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={onPasswordChange}
            error={error}
            variant="outlined"
            sx={{ ...inputStyle, mb: 2 }}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock
                    fontSize="small"
                    sx={{ color: "#0059f6", mr: 0.5 }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleShowPassword}
                    edge="end"
                    size="small"
                    sx={{ color: "#0059f6" }}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Box
              sx={{
                mb: 2.5,
                px: 1.5,
                py: 1,
                borderRadius: "6px",
                backgroundColor: "#fff1f1",
                border: "1px solid #ffd1d1",
              }}
            >
              <Typography
                sx={{
                  color: "#c62828",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Empresa o contraseña incorrecta.
              </Typography>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={rememberLogin}
                onChange={onRememberLoginChange}
                sx={{
                  color: "#c5cedd",
                  "&.Mui-checked": {
                    color: "#0059f6",
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#172044",
                }}
              >
                Recordar mis datos
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          <Button type="submit" variant="contained" fullWidth sx={loginButtonStyle}>
            Ingresar
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
